import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Link, useHistory} from 'react-router-dom';
import isEmail from 'validator/es/lib/isEmail';
import TextInput from '../../common/component/form/TextInput';
import {showMessage} from '../../message/action/messageActions';
import accountApi from '../api/accountApi';
import '../style/Registration.less';
import ErrorCode from '../../common/enums/ErrorCode';
import AccountRoles from '../enums/AccountRoles';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

function Registration() {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const [isValidated, setIsValidated] = useState(false);
    const [showTrainerRegistrationInfo, setShowTrainerRegistrationInfo] = useState(false);
    const [registrationError, setRegistrationError] = useState();
    const [captcha, setCaptcha] = useState();
    const [captchaImage, setCaptchaImage] = useState();
    const [account, setAccount] = useState({
        firstName: null,
        lastName: null,
        email: null
    });
    const [validation, setValidation] = useState();

    useEffect(() => {
        loadCaptcha();
    }, []);

    useEffect(() => {
        if (isValidated) {
            validate();
        }
    }, [account]);

    function loadCaptcha() {
        accountApi.getNewCaptcha().then((res) => {
                if (res.data.success) {
                    setCaptcha({
                        id: res.data.result.id,
                        enteredCaptcha: ''
                    });
                    setCaptchaImage(res.data.result.captchaImage);
                }
            }
        );
    }

    function validate() {
        const newValidation = {
            lastName: !!account.lastName,
            email: !!account.email && isEmail(account.email),
            serialNumber: !!account.serialNumber,
            captcha: !!captcha?.enteredCaptcha
        };

        setValidation(newValidation);
        setIsValidated(true);

        // Check if all fields are not empty
        return Object.values(newValidation).every(field => !!field);
    }

    function handleAccountChange(field, value) {
        setAccount({
            ...account,
            [field]: value
        });
    }

    function executeRegister() {
        const accountToSave = {...account};
        accountToSave.captcha = captcha;

        accountApi.register(accountToSave).then((res) => {
                if (res.data.success) {
                    setRegistrationError(null);
                    if (res.data.result.role.toUpperCase() === AccountRoles.TRAINER) {
                        setShowTrainerRegistrationInfo(true);
                    } else {
                        history.push('/login');
                        dispatch(showMessage('Registration.SUCCESS',
                            {username: account.email}));
                    }
                } else {
                    if (res.errorCode === ErrorCode.DATA_ALREADY_EXISTS.code) {
                        setRegistrationError('ACCOUNT_ALREADY_EXISTS');
                    } else if (res.errorCode === ErrorCode.CLUB_DOES_NOT_EXIST.code) {
                        setRegistrationError('INVALID_SERIAL_NUMBER');
                    } else if (res.errorCode === ErrorCode.NOT_SUPPORTED_IN_FREE_VERSION.code) {
                        setRegistrationError('MAX_ACCOUNTS_REACHED');
                    } else if (res.errorCode === ErrorCode.CAPTCHA_INVALID.code) {
                        // reload captcha on wrong try
                        loadCaptcha();
                        setRegistrationError('CAPTCHA_INVALID');
                    }
                }
            }
        );
    }

    function register() {
        if (validate()) {
            executeRegister();
        }
    }

    function handleCaptchaValueChange(value) {
        setCaptcha({
            ...captcha,
            enteredCaptcha: value
        });
        setValidation({
            ...validation,
            captcha: true
        });
    }

    const captchaImageString = `data:captchaImage/jpg;base64,${captchaImage}`;

    let serialNumberError = null;

    if (registrationError === 'INVALID_SERIAL_NUMBER') {
        serialNumberError = t('Error.CLUB_DOES_NOT_EXIST');
    } else if (registrationError === 'MAX_ACCOUNTS_REACHED') {
        serialNumberError = t('Error.MAX_ACCOUNTS_REACHED');
    }

    return (

        <>
            <div className="login-wrapper">
                <div className="mr-4 ml-4">
                    <div className="card">
                        <div className="card-body big-label">
                            <h1>
                                {t('Registration.TITLE')}
                            </h1>
                            {
                                showTrainerRegistrationInfo ?
                                    <>
                                        <p className="text-center mx-auto" style={{maxWidth: '500px'}}>{t('Registration.SUCCESS_FOR_TRAINER')}</p>
                                        <div className="text-center mt-3">
                                            <Link to="/login" className="btn btn-primary">{t('Confirmation.GO_TO_LOGIN')}</Link>
                                        </div>
                                    </>
                                    :
                                    <>
                                        <TextInput
                                            id="firstName"
                                            label={t('Login.FIRST_NAME')}
                                            value={account.firstName}
                                            onChange={value => handleAccountChange('firstName', value)}
                                        />
                                        <TextInput
                                            id="lastName"
                                            label={`${t('Account.LAST_NAME')}*`}
                                            value={account.lastName}
                                            onChange={value => handleAccountChange('lastName', value)}
                                            required={isValidated && !validation.lastName}
                                        />
                                        <TextInput
                                            errorMessage={registrationError === 'ACCOUNT_ALREADY_EXISTS' ? t('Error.ACCOUNT_ALREADY_EXISTS') : t('Account.VALID_EMAIL')}
                                            label={`${t('Account.EMAIL')}*`}
                                            value={account.email}
                                            required={registrationError === 'ACCOUNT_ALREADY_EXISTS' || isValidated && !validation.email}
                                            onChange={value => handleAccountChange('email', value)}
                                        />
                                        <TextInput
                                            label={`${t('Club.SERIAL_NUMBER_ALP')}*`}
                                            value={account.serialNumber}
                                            onChange={value => handleAccountChange('serialNumber', value)}
                                            required={registrationError === 'INVALID_SERIAL_NUMBER' || registrationError === 'MAX_ACCOUNTS_REACHED'
                                            || isValidated && !validation.serialNumber}
                                            errorMessage={serialNumberError}
                                        />
                                        <div className="mt-4 mb-3 captcha">
                                            <div className="text-center">
                                                <img src={captchaImageString} alt="captcha"/>
                                                <FontAwesomeIcon icon="sync-alt" className="ml-4 clickable" onClick={loadCaptcha}/>
                                            </div>
                                            <div className="mt-3">
                                                <TextInput
                                                    label={`${t('Registration.CAPTCHA')}*`}
                                                    value={captcha?.enteredCaptcha}
                                                    onChange={handleCaptchaValueChange}
                                                    required={registrationError === 'CAPTCHA_INVALID' || isValidated && !validation.captcha}
                                                    errorMessage={registrationError === 'CAPTCHA_INVALID' ? t('Error.CAPTCHA_INVALID') : null}
                                                />
                                            </div>
                                        </div>
                                        <div className="align-right">
                                            <button
                                                className="btn btn-primary"
                                                onClick={register}>
                                                {t('Button.REGISTER')}
                                            </button>
                                        </div>
                                        <div className="registration-to-login">
                                            {t('Registration.HAVE_ACCOUNT')} <Link to="/login">{t('Confirmation.GO_TO_LOGIN')}</Link>
                                        </div>
                                    </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Registration;
